import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  // UI State
  selectedElement: null, // { type, path, data }
  expandedNodes: [],
  leftPanelWidth: 280,
  rightPanelWidth: 320,
  showAIAssistant: false,
  showConditionalBuilder: false,
  showTableConfig: false,
  showRepeaterConfig: false,

  // Canvas State
  zoom: 1,
  draggedItem: null,
  hoveredPath: null,

  // Search/Filter
  searchQuery: '',
  filterType: 'all',
};

const rfpBuilderSlice = createSlice({
  name: 'rfpBuilder',
  initialState,
  reducers: {
    // Selection
    selectElement: (state, action) => {
      state.selectedElement = action.payload;
    },

    clearSelection: (state) => {
      state.selectedElement = null;
    },

    // Tree Expansion
    toggleNode: (state, action) => {
      const path = action.payload;
      const index = state.expandedNodes.indexOf(path);

      if (index > -1) {
        state.expandedNodes.splice(index, 1);
      } else {
        state.expandedNodes.push(path);
      }
    },

    expandAll: (state) => {
      // Will be populated with all node paths when template loads
      state.expandedNodes = ['__all__'];
    },

    collapseAll: (state) => {
      state.expandedNodes = [];
    },

    // Panel Sizes
    setLeftPanelWidth: (state, action) => {
      state.leftPanelWidth = Math.max(200, Math.min(400, action.payload));
    },

    setRightPanelWidth: (state, action) => {
      state.rightPanelWidth = Math.max(250, Math.min(500, action.payload));
    },

    // Modal Toggles
    toggleAIAssistant: (state) => {
      state.showAIAssistant = !state.showAIAssistant;
    },

    toggleConditionalBuilder: (state) => {
      state.showConditionalBuilder = !state.showConditionalBuilder;
    },

    toggleTableConfig: (state) => {
      state.showTableConfig = !state.showTableConfig;
    },

    toggleRepeaterConfig: (state) => {
      state.showRepeaterConfig = !state.showRepeaterConfig;
    },

    // Drag & Drop
    setDraggedItem: (state, action) => {
      state.draggedItem = action.payload;
    },

    setHoveredPath: (state, action) => {
      state.hoveredPath = action.payload;
    },

    // Search/Filter
    setSearchQuery: (state, action) => {
      state.searchQuery = action.payload;
    },

    setFilterType: (state, action) => {
      state.filterType = action.payload;
    },

    // Canvas
    setZoom: (state, action) => {
      state.zoom = Math.max(0.5, Math.min(2, action.payload));
    },

    zoomIn: (state) => {
      state.zoom = Math.min(2, state.zoom + 0.1);
    },

    zoomOut: (state) => {
      state.zoom = Math.max(0.5, state.zoom - 0.1);
    },

    resetZoom: (state) => {
      state.zoom = 1;
    },

    // Reset
    resetBuilder: () => initialState,
  },
});

export const {
  selectElement,
  clearSelection,
  toggleNode,
  expandAll,
  collapseAll,
  setLeftPanelWidth,
  setRightPanelWidth,
  toggleAIAssistant,
  toggleConditionalBuilder,
  toggleTableConfig,
  toggleRepeaterConfig,
  setDraggedItem,
  setHoveredPath,
  setSearchQuery,
  setFilterType,
  setZoom,
  zoomIn,
  zoomOut,
  resetZoom,
  resetBuilder,
} = rfpBuilderSlice.actions;

// Selectors
export const selectSelectedElement = (state) => state.rfpBuilder.selectedElement;
export const selectExpandedNodes = (state) => state.rfpBuilder.expandedNodes;
export const selectPanelWidths = (state) => ({
  left: state.rfpBuilder.leftPanelWidth,
  right: state.rfpBuilder.rightPanelWidth,
});
export const selectModals = (state) => ({
  aiAssistant: state.rfpBuilder.showAIAssistant,
  conditionalBuilder: state.rfpBuilder.showConditionalBuilder,
  tableConfig: state.rfpBuilder.showTableConfig,
  repeaterConfig: state.rfpBuilder.showRepeaterConfig,
});
export const selectDragState = (state) => ({
  draggedItem: state.rfpBuilder.draggedItem,
  hoveredPath: state.rfpBuilder.hoveredPath,
});
export const selectSearch = (state) => ({
  query: state.rfpBuilder.searchQuery,
  filterType: state.rfpBuilder.filterType,
});
export const selectZoom = (state) => state.rfpBuilder.zoom;

export default rfpBuilderSlice.reducer;
